import React from 'react';
import { PlusIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { Button } from '@oysterjs/ui/button';
import { AutoResizeTextarea } from '@oysterjs/ui/textarea';

/** The id keeps each input's state with its own row when an earlier row is removed. */
type GuidelineRow = { id: number; text: string };

// The server trims guidelines and drops blank ones, so this is what a save would keep.
export const guidelinesToSave = (rows: GuidelineRow[]): string[] =>
  rows.map((row) => row.text.trim()).filter(Boolean);

/** One editable row per guideline, with the most recently added row focused. */
export const useGuidelineRows = (guidelines: string[]) => {
  const nextRowId = React.useRef(0);
  const toRows = (texts: string[]) => texts.map((text) => ({ id: nextRowId.current++, text }));
  const [rows, setRows] = React.useState(() => toRows(guidelines));
  const [addedRowId, setAddedRowId] = React.useState<number | null>(null);

  return {
    rows,
    addedRowId,
    resetRows: (texts: string[]) => setRows(toRows(texts)),
    addRow: () => {
      const [row] = toRows(['']);
      setRows((current) => [...current, row]);
      setAddedRowId(row.id);
    },
    editRow: (id: number, text: string) =>
      setRows((current) => current.map((row) => (row.id === id ? { ...row, text } : row))),
    removeRow: (id: number) => setRows((current) => current.filter((row) => row.id !== id))
  };
};

// A guideline is one line that wraps to fit, so a pasted line break becomes a space.
const joinLines = (text: string) => text.replace(/\s*[\r\n]+\s*/g, ' ');

// Enter submits the form the way it would from a single-line input: through the form's
// first submit button, and only while that button is enabled.
const submitFormOnEnter = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
  const isComposingText = event.nativeEvent.isComposing;
  if (event.key !== 'Enter' || isComposingText) return;
  event.preventDefault();
  const defaultSubmitButton =
    event.currentTarget.form?.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (defaultSubmitButton && !defaultSubmitButton.disabled) defaultSubmitButton.click();
};

export const GuidelineRowInputs = ({
  guidelineRows,
  canEdit,
  maxRows
}: {
  guidelineRows: ReturnType<typeof useGuidelineRows>;
  canEdit: boolean;
  /** How many guidelines the server accepts, when it caps them. */
  maxRows?: number;
}) => {
  const isFull = maxRows !== undefined && guidelineRows.rows.length >= maxRows;
  return (
    <div className="space-y-2">
      {guidelineRows.rows.map((row, index) => (
        <div key={row.id} className="flex items-start gap-2">
          <AutoResizeTextarea
            aria-label={`Guideline ${index + 1}`}
            rows={1}
            resizable={false}
            style={{ minHeight: 0 }}
            value={row.text}
            onChange={(event) => guidelineRows.editRow(row.id, joinLines(event.target.value))}
            onKeyDown={submitFormOnEnter}
            disabled={!canEdit}
            autoFocus={row.id === guidelineRows.addedRowId}
          />
          {canEdit && (
            <Button
              plain
              aria-label="Remove guideline"
              onClick={() => guidelineRows.removeRow(row.id)}
            >
              <XMarkIcon />
            </Button>
          )}
        </div>
      ))}
      {canEdit && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <Button plain disabled={isFull} onClick={guidelineRows.addRow}>
            <PlusIcon />
            Add guideline
          </Button>
          {isFull && (
            <p className="m-0 text-sm text-neutral-500 dark:text-neutral-400">
              Up to {maxRows} guidelines.
            </p>
          )}
        </div>
      )}
    </div>
  );
};
